import React, { useCallback, useMemo } from 'react';

import { dotFormatter, getLevelConditionValues, isAmountBasedCondition, isMobile, resizeContent, setTranslationValues } from '../../../../../helpers';
import { LevelConditionLongKeys, T } from '../../../../../constants';
import { CashbackWinType } from '../../../../../enums';
import { useTranslation } from '../../../../../hooks';
import { CashbackLevel } from '../../../../../models';
import { RewardsProgressBar } from '../progress-bar';
import { RewardsGameTile } from '../game-tile';

import './style.scss';

interface MissionDetailsProps {
  level: CashbackLevel;
  color: string;
  currency: string;
  onClose: () => void;
  onSeeAll: () => void;
  onGameRedirect: () => void;
}

export const MissionDetails: React.FC<MissionDetailsProps> = ({
  level,
  color,
  currency,
  onClose,
  onSeeAll,
  onGameRedirect
}) => {

  const t = useTranslation();

  const {
    winType,
    rewardAmount,
    freeBetCount,
    levelConditionType,
    goalAmount,
    goalCount,
    currentAmount,
    currentCount,
    progress,
    accumulationMinBetAmount,
    accumulationMinOdd,
    games
  } = level;

  const amountBased = isAmountBasedCondition(levelConditionType);

  const rewardText = useMemo(() => {
    const isFreebet = winType === CashbackWinType.Freebet;
    if (isFreebet && freeBetCount) {
      return setTranslationValues(t(T.REWARDS_FREEBET_REWARD), {
        count: freeBetCount,
        amount: `${dotFormatter(rewardAmount)} ${currency}`
      });
    }
    return `${dotFormatter(rewardAmount)} ${currency}`;
  }, [t, winType, freeBetCount, rewardAmount, currency]);

  const descriptionContent = useMemo(() => ({
    __html: setTranslationValues(
      t(LevelConditionLongKeys[levelConditionType]),
      getLevelConditionValues(level, currency, 'mission-details-total-text-amount')
    )
  }), [t, level, currency, levelConditionType]);

  const popupRefCallback = useCallback((element: HTMLDivElement) => {
    if (element && !isMobile()) {
      resizeContent({ target: element });
      window.addEventListener('resize', () => resizeContent({ target: element }));
    }
  }, []);

  const strokeText = (
    <>
      {
        amountBased
          ? `${dotFormatter(currentAmount)}/${dotFormatter(goalAmount)}`
          : `${dotFormatter(currentCount)}/${dotFormatter(goalCount)}`
      }
      {amountBased && <span className="stroke-text-curency">{currency}</span>}
    </>
  );

  const hasQualifyingConditions = accumulationMinBetAmount !== null || accumulationMinOdd !== null;

  return (
    <div className="mission-details-wrapper open">
        <div className="mission-details-wrapper-container" ref={popupRefCallback}>
            <div className={`mission-widget ${color}`}>
                <div className="level-block">
                    <span className="level-block-text">{t(T.LVL)} {level.level}</span>
                </div>
                <button className="close-btn-mission-details" type="button" onClick={onClose}>
                    <span className="icon-close mission-details-cloe-icon" />
                </button>
                <div className="mission-widget-content">
                    <p className="mission-details-text">{t(T.MISSION_DETAILS)}</p>
                    <div className="reward-amount-block">
                        <p className="reward-text-mission">{t(T.REWARD)}</p>
                        <p className="reward-text-amount">{rewardText}</p>
                    </div>
                    <RewardsProgressBar
                      className="mission-details-stroke"
                      progress={progress}
                      text={strokeText}
                    />
                    <p className="mission-details-total-text" dangerouslySetInnerHTML={descriptionContent} />
                    {
                      hasQualifyingConditions && (
                        <div className="qualifing-conditions-block">
                            <p className="qualifing-conditions-text">{t(T.QUALIFYING_CONDITIONS)}</p>
                            <div className="qualifing-conditions-block-content">
                                {
                                  accumulationMinBetAmount !== null && (
                                    <div className="qualifing-conditions-block-content-items">
                                        <p className="qualifing-conditions-block-content-items-text">{t(T.REWARDS_MIN_BET)}</p>
                                        <p className="qualifing-conditions-block-content-items-amount">{dotFormatter(accumulationMinBetAmount)} {currency}</p>
                                    </div>
                                  )
                                }
                                {
                                  accumulationMinOdd !== null && (
                                    <div className="qualifing-conditions-block-content-items">
                                        <p className="qualifing-conditions-block-content-items-text">{t(T.REWARDS_MIN_ODD)}</p>
                                        <p className="qualifing-conditions-block-content-items-amount">x{accumulationMinOdd}</p>
                                    </div>
                                  )
                                }
                            </div>
                        </div>
                      )
                    }
                    {
                      !!games?.length && (
                        <div className="eligible-games-block">
                            <p className="eligible-games-text">{t(T.REWARDS_ELIGIBLE_GAMES)}</p>
                            <div className="see-all-block">
                                <button type="button" className="see-all-more-games-btn" onClick={onSeeAll}>{t(T.SEE_ALL)}</button>
                            </div>
                            <div className="suggested-block-content">
                                <div className="suggested-block-content-scroll">
                                    <div className="suggested-block-content-inner">
                                        {
                                          games.map((game) => (
                                            <RewardsGameTile
                                              key={game.gameId}
                                              game={game}
                                              onRedirect={onGameRedirect}
                                            />
                                          ))
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                      )
                    }
                </div>
            </div>
        </div>
    </div>
  );
};
